import PropTypes from 'prop-types';

import { trans } from '@spotahome/soyuz/client';

import { Mediator } from '@spotahome/soyuz-mediator';

import { formatPrice } from '@spotahome/soyuz-currency';

import DSIcon from '../../../base/DSIcon';

import {
  APPLY_ON_INCLUDED,
  COST_TYPE_CUSTOM_FEE,
  COST_TYPE_CUSTOM_SERVICE,
  COST_TYPE_CUSTOM_TAX
} from '../../constants/costs';

import { OPEN_GUARANTEE_MODAL } from '../utils/constants';

import { ListingCostsShape } from '../../constants/shapes';

import PaymentTimelineCard from './PaymentTimelineCard';
import { FREQUENCY_PRICE_DICTIONARY, ICONS_DICTIONARY } from './constants';
import IncludedBadge from './IncludedBadge';

import styles from './Extra.module.scss';

const isCustomCost = costType =>
  [
    COST_TYPE_CUSTOM_SERVICE,
    COST_TYPE_CUSTOM_TAX,
    COST_TYPE_CUSTOM_FEE
  ].includes(costType);

const isDescriptionKeyValid = type =>
  trans(`payment.timeline.cost.label.${type}.description`) !==
  `payment.timeline.cost.label.${type}.description`;

const Extra = ({ costs = {}, currencyCode = 'EUR' }) => {
  const handleCostClick = cost => () => {
    let price;
    const isIncluded = cost.applyOn === APPLY_ON_INCLUDED;
    if (cost.value && !isIncluded) {
      const formattedPrice = formatPrice(cost.value, currencyCode);
      const frequencyLabel =
        FREQUENCY_PRICE_DICTIONARY[cost.applyOn] ||
        FREQUENCY_PRICE_DICTIONARY[cost.frequency];

      price = frequencyLabel
        ? trans(frequencyLabel, { price: formattedPrice })
        : formattedPrice;
    }

    Mediator.publish(OPEN_GUARANTEE_MODAL, {
      title: cost.name || (
        <p className={styles.extra__title}>
          {ICONS_DICTIONARY[cost.type] && (
            <DSIcon name={ICONS_DICTIONARY[cost.type]} />
          )}
          <span className={styles['extra__title-type']}>
            {trans(`payment.timeline.cost.label.${cost.type}`)}
          </span>
        </p>
      ),
      body: cost.description || (
        <>
          {!isCustomCost(cost.type) && isDescriptionKeyValid(cost.type) ? (
            <p className={styles.extra__description}>
              {trans(`payment.timeline.cost.label.${cost.type}.description`)}
            </p>
          ) : null}
          {price ? <p className={styles.extra__price}>{price}</p> : null}
          {isIncluded ? (
            <IncludedBadge className={styles.extra__price} />
          ) : null}
          <p
            dangerouslySetInnerHTML={{
              __html: trans('payment.timeline.cost.label.extra.ask-landlord')
            }}
          />
        </>
      )
    });
  };

  return costs?.costs?.length > 0 ? (
    <PaymentTimelineCard
      title={trans('payment.timeline.section.extra.title')}
      subtitle={trans('payment.timeline.section.extra.subtitle')}
      outlined={false}
      gapSpacing="big"
      tags={costs?.costs?.map(cost => ({
        label: isCustomCost(cost.type)
          ? cost.name
          : trans(`payment.timeline.cost.label.${cost.type}`),
        renderLeadingIcon: () =>
          ICONS_DICTIONARY[cost.type] ? (
            <DSIcon name={ICONS_DICTIONARY[cost.type]} />
          ) : null,
        onClick: handleCostClick(cost),
        isSpaced: true,
        underlined: true,
        size: 'big',
        colorSchema: 'light'
      }))}
    />
  ) : null;
};

Extra.propTypes = {
  costs: ListingCostsShape,
  currencyCode: PropTypes.string
};

export default Extra;
